import React from "react"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Button } from "../../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card"
import { Badge } from "../../components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table"
import {
  Users,
  Clock,
  Calculator,
  FileText,
  Settings,
  BarChart3,
  Shield,
  Globe,
  CheckCircle,
  ArrowRight,
  Building2,
  Wallet,
  UserCog,
  UserPlus,
  History,
  Network,
  CalendarClock,
  CalendarCheck,
  Timer,
  Receipt,
  Landmark,
  ShieldCheck,
  CalendarDays,
  ClipboardCheck,
  PieChart,
  Sliders,
  FolderCog,
  CalendarRange,
  FileBarChart,
  FileSpreadsheet,
  Activity,
  Camera,
  ScanFace,
  Fingerprint,
  Cpu,
  Database,
  BadgeCheck,
  CircleDollarSign,
  FileCheck,
  Send,
  Banknote,
  ChevronRight,
  Sparkles,
  HeartPulse,
  MinusCircle,
  ClockArrowUp,
  AlarmClockMinus,
  Percent,
  Target,
  Zap,
  Layers,
  Gift,
} from "lucide-react"
import i18n from "../../utils/Language"
import { normalizeLang } from "../../utils/normalizeLang"
import { projectStatusRows } from "../../data/projectStatus"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu"

const LandingPage = () => {
  const { t } = useTranslation("common")
  const [language, setLanguage] = React.useState(
    normalizeLang(i18n.language) || normalizeLang(localStorage.getItem("lang")) || "vi"
  )

  const statusVariant = {
    done: "success",
    partial: "warning",
    todo: "secondary",
    na: "outline",
  }

  const handleSetLang = (lang) => {
    const langNormalized = normalizeLang(lang) || "en"
    localStorage.setItem("lang", langNormalized)
    i18n.changeLanguage(langNormalized).catch(() => {})
    setLanguage(langNormalized)
  }

  React.useEffect(() => {
    const syncLanguage = (lng) => {
      const normalized =
        normalizeLang(lng) || normalizeLang(localStorage.getItem("lang")) || "vi"
      setLanguage(normalized)
    }

    syncLanguage(i18n.resolvedLanguage || i18n.language)

    i18n.on("languageChanged", syncLanguage)
    return () => {
      i18n.off("languageChanged", syncLanguage)
    }
  }, [])

  const languageLabels = {
    en: "English",
    ja: "日本語",
    vi: "Tiếng Việt",
  }

  const features = [
    {
      icon: Users,
      titleKey: "landing.features.employeeManagement.title",
      descKey: "landing.features.employeeManagement.desc",
      color: "from-primary to-primary-700",
      bgColor: "bg-primary-50",
      highlights: [
        { icon: UserPlus, key: "landing.features.employeeManagement.highlights.profiles" },
        { icon: History, key: "landing.features.employeeManagement.highlights.history" },
        { icon: Network, key: "landing.features.employeeManagement.highlights.structure" },
      ],
    },
    {
      icon: Clock,
      titleKey: "landing.features.timeAttendance.title",
      descKey: "landing.features.timeAttendance.desc",
      color: "from-tertiary-blue to-blue-700",
      bgColor: "bg-blue-50",
      highlights: [
        { icon: CalendarClock, key: "landing.features.timeAttendance.highlights.realtime" },
        { icon: CalendarCheck, key: "landing.features.timeAttendance.highlights.shifts" },
        { icon: Timer, key: "landing.features.timeAttendance.highlights.overtime" },
      ],
    },
    {
      icon: Calculator,
      titleKey: "landing.features.payrollProcessing.title",
      descKey: "landing.features.payrollProcessing.desc",
      color: "from-tertiary-green to-green-700",
      bgColor: "bg-green-50",
      highlights: [
        { icon: Receipt, key: "landing.features.payrollProcessing.highlights.automated" },
        { icon: Landmark, key: "landing.features.payrollProcessing.highlights.tax" },
        { icon: ShieldCheck, key: "landing.features.payrollProcessing.highlights.insurance" },
      ],
    },
    {
      icon: FileText,
      titleKey: "landing.features.leaveManagement.title",
      descKey: "landing.features.leaveManagement.desc",
      color: "from-tertiary-orange to-orange-600",
      bgColor: "bg-orange-50",
      highlights: [
        { icon: CalendarDays, key: "landing.features.leaveManagement.highlights.requests" },
        { icon: ClipboardCheck, key: "landing.features.leaveManagement.highlights.approvals" },
        { icon: PieChart, key: "landing.features.leaveManagement.highlights.balance" },
      ],
    },
    {
      icon: Settings,
      titleKey: "landing.features.systemConfiguration.title",
      descKey: "landing.features.systemConfiguration.desc",
      color: "from-tertiary-purple to-purple-700",
      bgColor: "bg-purple-50",
      highlights: [
        { icon: Sliders, key: "landing.features.systemConfiguration.highlights.salary" },
        { icon: FolderCog, key: "landing.features.systemConfiguration.highlights.departments" },
        { icon: CalendarRange, key: "landing.features.systemConfiguration.highlights.shifts" },
      ],
    },
    {
      icon: BarChart3,
      titleKey: "landing.features.reportsAnalytics.title",
      descKey: "landing.features.reportsAnalytics.desc",
      color: "from-tertiary-red to-red-600",
      bgColor: "bg-red-50",
      highlights: [
        { icon: FileBarChart, key: "landing.features.reportsAnalytics.highlights.payroll" },
        { icon: FileSpreadsheet, key: "landing.features.reportsAnalytics.highlights.attendance" },
        { icon: Activity, key: "landing.features.reportsAnalytics.highlights.workforce" },
      ],
    },
  ]

  const workflows = [
    {
      step: 1,
      icon: Settings,
      titleKey: "landing.workflow.systemSetup.title",
      descKey: "landing.workflow.systemSetup.desc",
      color: "from-tertiary-purple to-purple-700",
      bgColor: "bg-purple-50",
      highlights: [
        { icon: Building2, key: "landing.workflow.systemSetup.highlights.company" },
        { icon: FolderCog, key: "landing.workflow.systemSetup.highlights.departments" },
        { icon: Shield, key: "landing.workflow.systemSetup.highlights.permissions" },
      ],
    },
    {
      step: 2,
      icon: Wallet,
      titleKey: "landing.workflow.salaryStructure.title",
      descKey: "landing.workflow.salaryStructure.desc",
      color: "from-tertiary-green to-green-700",
      bgColor: "bg-green-50",
      highlights: [
        { icon: CircleDollarSign, key: "landing.workflow.salaryStructure.highlights.components" },
        { icon: Receipt, key: "landing.workflow.salaryStructure.highlights.allowances" },
        { icon: FileText, key: "landing.workflow.salaryStructure.highlights.templates" },
      ],
    },
    {
      step: 3,
      icon: Users,
      titleKey: "landing.workflow.employeeRegistration.title",
      descKey: "landing.workflow.employeeRegistration.desc",
      color: "from-primary to-primary-700",
      bgColor: "bg-primary-50",
      highlights: [
        { icon: UserPlus, key: "landing.workflow.employeeRegistration.highlights.profiles" },
        { icon: ScanFace, key: "landing.workflow.employeeRegistration.highlights.faceRegister" },
        { icon: Fingerprint, key: "landing.workflow.employeeRegistration.highlights.biometric" },
      ],
    },
    {
      step: 4,
      icon: Camera,
      titleKey: "landing.workflow.dataCollection.title",
      descKey: "landing.workflow.dataCollection.desc",
      color: "from-tertiary-blue to-blue-700",
      bgColor: "bg-blue-50",
      isHighlighted: true,
      highlights: [
        { icon: ScanFace, key: "landing.workflow.dataCollection.highlights.faceRecognition" },
        { icon: Cpu, key: "landing.workflow.dataCollection.highlights.aiProcessing" },
        { icon: Database, key: "landing.workflow.dataCollection.highlights.autoSync" },
      ],
    },
    {
      step: 5,
      icon: Calculator,
      titleKey: "landing.workflow.payrollCalculation.title",
      descKey: "landing.workflow.payrollCalculation.desc",
      color: "from-tertiary-orange to-orange-600",
      bgColor: "bg-orange-50",
      highlights: [
        { icon: Cpu, key: "landing.workflow.payrollCalculation.highlights.automated" },
        { icon: Landmark, key: "landing.workflow.payrollCalculation.highlights.tax" },
        { icon: ShieldCheck, key: "landing.workflow.payrollCalculation.highlights.compliance" },
      ],
    },
    {
      step: 6,
      icon: Send,
      titleKey: "landing.workflow.reviewApprove.title",
      descKey: "landing.workflow.reviewApprove.desc",
      color: "from-tertiary-red to-red-600",
      bgColor: "bg-red-50",
      highlights: [
        { icon: FileCheck, key: "landing.workflow.reviewApprove.highlights.review" },
        { icon: BadgeCheck, key: "landing.workflow.reviewApprove.highlights.approval" },
        { icon: Banknote, key: "landing.workflow.reviewApprove.highlights.payment" },
      ],
    },
  ]

  const roles = [
    {
      icon: Shield,
      titleKey: "landing.roles.systemAdmin",
      color: "bg-tertiary-red",
    },
    {
      icon: UserCog,
      titleKey: "landing.roles.hrManager",
      color: "bg-primary",
    },
    {
      icon: Calculator,
      titleKey: "landing.roles.payrollSpecialist",
      color: "bg-tertiary-blue",
    },
    {
      icon: Building2,
      titleKey: "landing.roles.departmentManager",
      color: "bg-tertiary-purple",
    },
    {
      icon: Users,
      titleKey: "landing.roles.employee",
      color: "bg-tertiary-orange",
    },
  ]

  const stats = [
    { value: "17+", labelKey: "landing.stats.featurePages" },
    { value: "7", labelKey: "landing.stats.mainProcesses" },
    { value: "5", labelKey: "landing.stats.userRoles" },
    { value: "3", labelKey: "landing.stats.languages" },
  ]

  const flexibilityFeatures = [
    {
      icon: HeartPulse,
      titleKey: "landing.flexibility.insurance.title",
      descKey: "landing.flexibility.insurance.desc",
      color: "from-rose-500 to-rose-700",
      bgColor: "bg-rose-50",
      iconColor: "text-rose-600",
      features: [
        { key: "landing.flexibility.insurance.features.bhxh" },
        { key: "landing.flexibility.insurance.features.bhyt" },
        { key: "landing.flexibility.insurance.features.bhtn" },
        { key: "landing.flexibility.insurance.features.pit" },
      ],
      stats: { value: "7+", labelKey: "landing.flexibility.insurance.statsLabel" },
    },
    {
      icon: Gift,
      titleKey: "landing.flexibility.allowance.title",
      descKey: "landing.flexibility.allowance.desc",
      color: "from-emerald-500 to-emerald-700",
      bgColor: "bg-emerald-50",
      iconColor: "text-emerald-600",
      features: [
        { key: "landing.flexibility.allowance.features.types" },
        { key: "landing.flexibility.allowance.features.calculation" },
        { key: "landing.flexibility.allowance.features.conditions" },
        { key: "landing.flexibility.allowance.features.autoApply" },
      ],
      stats: { value: "3", labelKey: "landing.flexibility.allowance.statsLabel" },
    },
    {
      icon: MinusCircle,
      titleKey: "landing.flexibility.deduction.title",
      descKey: "landing.flexibility.deduction.desc",
      color: "from-amber-500 to-amber-700",
      bgColor: "bg-amber-50",
      iconColor: "text-amber-600",
      features: [
        { key: "landing.flexibility.deduction.features.targeted" },
        { key: "landing.flexibility.deduction.features.bulk" },
        { key: "landing.flexibility.deduction.features.filters" },
        { key: "landing.flexibility.deduction.features.flexible" },
      ],
      stats: { value: "3", labelKey: "landing.flexibility.deduction.statsLabel" },
    },
    {
      icon: ClockArrowUp,
      titleKey: "landing.flexibility.overtime.title",
      descKey: "landing.flexibility.overtime.desc",
      color: "from-blue-500 to-blue-700",
      bgColor: "bg-blue-50",
      iconColor: "text-blue-600",
      features: [
        { key: "landing.flexibility.overtime.features.types" },
        { key: "landing.flexibility.overtime.features.breaks" },
        { key: "landing.flexibility.overtime.features.import" },
        { key: "landing.flexibility.overtime.features.groups" },
      ],
      stats: { value: "7", labelKey: "landing.flexibility.overtime.statsLabel" },
    },
    {
      icon: AlarmClockMinus,
      titleKey: "landing.flexibility.lateEarly.title",
      descKey: "landing.flexibility.lateEarly.desc",
      color: "from-violet-500 to-violet-700",
      bgColor: "bg-violet-50",
      iconColor: "text-violet-600",
      features: [
        { key: "landing.flexibility.lateEarly.features.late" },
        { key: "landing.flexibility.lateEarly.features.early" },
        { key: "landing.flexibility.lateEarly.features.return" },
        { key: "landing.flexibility.lateEarly.features.shiftAware" },
      ],
      stats: { value: "3", labelKey: "landing.flexibility.lateEarly.statsLabel" },
    },
  ]

  const StatusBadge = ({ status }) => (
    <Badge variant={statusVariant[status] || "outline"}>
      {t(`landing.projectStatus.status.${status}`)}
    </Badge>
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="h-8 w-8 text-primary" />
            <span className="text-xl font-bold text-primary">Mercury TNA</span>
          </div>
          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Globe className="h-4 w-4" />
                  {languageLabels[language]}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault()
                    handleSetLang("en")
                  }}
                >
                  English
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault()
                    handleSetLang("ja")
                  }}
                >
                  日本語
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault()
                    handleSetLang("vi")
                  }}
                >
                  Tiếng Việt
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Link to="/login">
              <Button className="bg-primary hover:bg-primary-700">
                {t("landing.login")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container py-20 md:py-32">
        <div className="flex flex-col items-center text-center">
          <Badge variant="secondary" className="mb-4 bg-primary-50 text-primary border-primary/20">
            {t("landing.badge")}
          </Badge>
          <h1 className="text-4xl font-bold tracking-tight text-neutral-900 sm:text-5xl md:text-6xl lg:text-7xl">
            <span className="block">{t("landing.title")}</span>
            <span className="block text-primary mt-2">{t("landing.subtitle")}</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-neutral-600 md:text-xl">
            {t("landing.description")}
          </p>
          <div className="mt-10 flex flex-col gap-4 sm:flex-row">
            <Link to="/login">
              <Button size="lg" className="bg-primary hover:bg-primary-700 text-lg px-8">
                {t("landing.getStarted")}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Button
              size="lg"
              variant="outline"
              className="text-lg px-8 border-primary text-primary hover:bg-primary-50"
            >
              {t("landing.learnMore")}
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="container py-12">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {stats.map((stat, index) => (
            <Card key={index} className="text-center border-none shadow-lg bg-white">
              <CardContent className="pt-6">
                <div className="text-4xl font-bold text-primary">{stat.value}</div>
                <div className="mt-2 text-sm text-neutral-600">{t(stat.labelKey)}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="container py-20">
        <div className="text-center mb-16">
          <Badge variant="secondary" className="mb-4 bg-primary-50 text-primary border-primary/20">
            {t("landing.featuresLabel")}
          </Badge>
          <h2 className="text-3xl font-bold text-neutral-900 sm:text-4xl md:text-5xl">
            {t("landing.keyFeatures")}
          </h2>
          <p className="mt-4 text-lg text-neutral-600 max-w-2xl mx-auto">
            {t("landing.keyFeaturesDesc")}
          </p>
        </div>
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, index) => (
            <Card
              key={index}
              className="group relative overflow-hidden hover:shadow-xl transition-all duration-500 border-neutral-200 hover:border-transparent bg-white"
            >
              {/* Gradient overlay on hover */}
              <div
                className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-[0.03] transition-opacity duration-500`}
              />

              <CardHeader className="pb-4">
                <div className="flex items-start justify-between">
                  <div
                    className={`mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl ${feature.bgColor} group-hover:bg-gradient-to-br ${feature.color} transition-all duration-500`}
                  >
                    <feature.icon className="h-7 w-7 text-neutral-700 group-hover:text-white transition-colors duration-500" />
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-300 group-hover:text-primary group-hover:translate-x-1 transition-all duration-300" />
                </div>
                <CardTitle className="text-xl font-semibold text-neutral-900 group-hover:text-neutral-900">
                  {t(feature.titleKey)}
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-4">
                <CardDescription className="text-neutral-600 text-base leading-relaxed">
                  {t(feature.descKey)}
                </CardDescription>

                {/* Feature Highlights */}
                <div className="pt-4 border-t border-neutral-100">
                  <ul className="space-y-3">
                    {feature.highlights.map((highlight, hIndex) => (
                      <li
                        key={hIndex}
                        className="flex items-center gap-3 text-sm text-neutral-600 group-hover:text-neutral-700 transition-colors"
                      >
                        <div
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${feature.bgColor} group-hover:bg-opacity-70 transition-colors`}
                        >
                          <highlight.icon className="h-4 w-4 text-neutral-600" />
                        </div>
                        <span>{t(highlight.key)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Workflow Section */}
      <section className="bg-gradient-to-b from-neutral-100 to-neutral-200/50 py-20">
        <div className="container">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4 bg-tertiary-blue/10 text-tertiary-blue border-tertiary-blue/20">
              <Sparkles className="h-3 w-3 mr-1" />
              {t("landing.workflowLabel")}
            </Badge>
            <h2 className="text-3xl font-bold text-neutral-900 sm:text-4xl md:text-5xl">
              {t("landing.howItWorks")}
            </h2>
            <p className="mt-4 text-lg text-neutral-600 max-w-2xl mx-auto">
              {t("landing.howItWorksDesc")}
            </p>
          </div>

          {/* Camera AI Highlight Banner */}
          <div className="mb-12 rounded-2xl bg-gradient-to-r from-tertiary-blue via-blue-600 to-tertiary-purple p-[2px]">
            <div className="rounded-2xl bg-white p-6 md:p-8">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-tertiary-blue to-tertiary-purple text-white">
                  <Camera className="h-10 w-10" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                    <Badge className="bg-tertiary-blue/10 text-tertiary-blue border-tertiary-blue/20">
                      <Cpu className="h-3 w-3 mr-1" />
                      AI Powered
                    </Badge>
                  </div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">
                    {t("landing.cameraAI.title")}
                  </h3>
                  <p className="text-neutral-600">
                    {t("landing.cameraAI.desc")}
                  </p>
                </div>
                <div className="flex flex-wrap justify-center gap-3">
                  <div className="flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-sm text-tertiary-blue">
                    <ScanFace className="h-4 w-4" />
                    {t("landing.cameraAI.features.faceRecognition")}
                  </div>
                  <div className="flex items-center gap-2 rounded-full bg-purple-50 px-4 py-2 text-sm text-tertiary-purple">
                    <Fingerprint className="h-4 w-4" />
                    {t("landing.cameraAI.features.biometric")}
                  </div>
                  <div className="flex items-center gap-2 rounded-full bg-green-50 px-4 py-2 text-sm text-tertiary-green">
                    <Database className="h-4 w-4" />
                    {t("landing.cameraAI.features.autoSync")}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Workflow Steps */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {workflows.map((workflow, index) => (
              <div key={index} className="relative group">
                {/* Connection line for desktop */}
                {index < workflows.length - 1 && index !== 2 && (
                  <div className="hidden lg:block absolute top-12 left-full w-6 z-10">
                    <ChevronRight className="h-6 w-6 text-neutral-300" />
                  </div>
                )}

                <Card
                  className={`h-full border-none shadow-md hover:shadow-xl transition-all duration-500 ${
                    workflow.isHighlighted
                      ? "ring-2 ring-tertiary-blue/30 bg-gradient-to-br from-blue-50/50 to-white"
                      : "bg-white"
                  }`}
                >
                  {/* Step indicator and highlighted badge */}
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${workflow.color} text-white font-bold text-lg shadow-lg`}
                        >
                          {workflow.step}
                        </div>
                        <div
                          className={`flex h-12 w-12 items-center justify-center rounded-2xl ${workflow.bgColor} group-hover:bg-gradient-to-br ${workflow.color} transition-all duration-500`}
                        >
                          <workflow.icon className="h-6 w-6 text-neutral-700 group-hover:text-white transition-colors duration-500" />
                        </div>
                      </div>
                      {workflow.isHighlighted && (
                        <Badge className="bg-tertiary-blue/10 text-tertiary-blue border-tertiary-blue/20 text-xs">
                          <Cpu className="h-3 w-3 mr-1" />
                          AI
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-lg font-semibold text-neutral-900">
                      {t(workflow.titleKey)}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <CardDescription className="text-neutral-600 leading-relaxed">
                      {t(workflow.descKey)}
                    </CardDescription>

                    {/* Workflow Highlights */}
                    <div className="pt-4 border-t border-neutral-100">
                      <ul className="space-y-2">
                        {workflow.highlights.map((highlight, hIndex) => (
                          <li
                            key={hIndex}
                            className="flex items-center gap-3 text-sm text-neutral-600"
                          >
                            <div
                              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${workflow.bgColor}`}
                            >
                              <highlight.icon className="h-3.5 w-3.5 text-neutral-600" />
                            </div>
                            <span>{t(highlight.key)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Flexibility Section */}
      <section className="container py-20">
        <div className="text-center mb-16">
          <Badge variant="secondary" className="mb-4 bg-gradient-to-r from-emerald-50 to-blue-50 text-emerald-700 border-emerald-200">
            <Zap className="h-3 w-3 mr-1" />
            {t("landing.flexibilityLabel")}
          </Badge>
          <h2 className="text-3xl font-bold text-neutral-900 sm:text-4xl md:text-5xl">
            {t("landing.flexibility.title")}
          </h2>
          <p className="mt-4 text-lg text-neutral-600 max-w-3xl mx-auto">
            {t("landing.flexibility.desc")}
          </p>
        </div>

        {/* Flexibility Feature Cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {flexibilityFeatures.map((feature, index) => (
            <Card
              key={index}
              className="group relative overflow-hidden hover:shadow-2xl transition-all duration-500 border-none bg-white"
            >
              {/* Gradient top border */}
              <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${feature.color}`} />

              <CardHeader className="pb-3 pt-6">
                <div className="flex items-center justify-between mb-3">
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-2xl ${feature.bgColor} group-hover:scale-110 transition-transform duration-300`}
                  >
                    <feature.icon className={`h-6 w-6 ${feature.iconColor}`} />
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-bold bg-gradient-to-r ${feature.color} bg-clip-text text-transparent`}>
                      {feature.stats.value}
                    </div>
                    <div className="text-xs text-neutral-500">{t(feature.stats.labelKey)}</div>
                  </div>
                </div>
                <CardTitle className="text-lg font-semibold text-neutral-900">
                  {t(feature.titleKey)}
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-3">
                <CardDescription className="text-neutral-600 text-sm leading-relaxed min-h-[60px]">
                  {t(feature.descKey)}
                </CardDescription>

                {/* Feature List */}
                <ul className="space-y-2 pt-3 border-t border-neutral-100">
                  {feature.features.map((item, fIndex) => (
                    <li
                      key={fIndex}
                      className="flex items-center gap-2 text-xs text-neutral-600"
                    >
                      <CheckCircle className={`h-3.5 w-3.5 shrink-0 ${feature.iconColor}`} />
                      <span>{t(item.key)}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Flexibility Highlight Banner */}
        <div className="mt-12 rounded-2xl bg-gradient-to-r from-emerald-500 via-teal-500 to-blue-500 p-[2px]">
          <div className="rounded-2xl bg-white p-8">
            <div className="flex flex-col lg:flex-row items-center gap-8">
              <div className="flex-1 text-center lg:text-left">
                <h3 className="text-2xl font-bold text-neutral-900 mb-3">
                  {t("landing.flexibility.highlight.title")}
                </h3>
                <p className="text-neutral-600 max-w-2xl">
                  {t("landing.flexibility.highlight.desc")}
                </p>
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="flex items-center gap-3 rounded-xl bg-emerald-50 px-5 py-3">
                  <Percent className="h-6 w-6 text-emerald-600" />
                  <div>
                    <div className="font-semibold text-neutral-900">{t("landing.flexibility.highlight.rates")}</div>
                    <div className="text-xs text-neutral-500">{t("landing.flexibility.highlight.ratesDesc")}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-xl bg-blue-50 px-5 py-3">
                  <Target className="h-6 w-6 text-blue-600" />
                  <div>
                    <div className="font-semibold text-neutral-900">{t("landing.flexibility.highlight.scope")}</div>
                    <div className="text-xs text-neutral-500">{t("landing.flexibility.highlight.scopeDesc")}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-xl bg-violet-50 px-5 py-3">
                  <Layers className="h-6 w-6 text-violet-600" />
                  <div>
                    <div className="font-semibold text-neutral-900">{t("landing.flexibility.highlight.templates")}</div>
                    <div className="text-xs text-neutral-500">{t("landing.flexibility.highlight.templatesDesc")}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Status Section */}
      <section className="bg-white py-20">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-neutral-900 sm:text-4xl">
              {t("landing.projectStatus.title")}
            </h2>
            <p className="mt-4 text-lg text-neutral-600 max-w-3xl mx-auto">
              {t("landing.projectStatus.desc")}
            </p>
          </div>

          <Card className="border-neutral-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{t("landing.projectStatus.tableTitle")}</CardTitle>
              <CardDescription>{t("landing.projectStatus.tableDesc")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-auto max-h-[560px] rounded-md border border-neutral-200">
                <Table>
                  <TableHeader className="sticky top-0 bg-white z-10">
                    <TableRow>
                      <TableHead className="whitespace-nowrap">
                        {t("landing.projectStatus.columns.category")}
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        {t("landing.projectStatus.columns.srs")}
                      </TableHead>
                      <TableHead className="min-w-[220px]">
                        {t("landing.projectStatus.columns.feature")}
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        {t("landing.projectStatus.columns.logic")}
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        {t("landing.projectStatus.columns.ui")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {projectStatusRows.map((row) => (
                      <TableRow key={row.srsFile}>
                        <TableCell className="font-medium">{row.category}</TableCell>
                        <TableCell className="font-mono text-xs whitespace-nowrap">
                          {row.srsFile}
                        </TableCell>
                        <TableCell>{row.feature}</TableCell>
                        <TableCell>
                          <StatusBadge status={row.logic} />
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={row.ui} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* User Roles Section */}
      <section className="bg-neutral-100/50 py-20">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-neutral-900 sm:text-4xl">{t("landing.roleBasedAccess")}</h2>
            <p className="mt-4 text-lg text-neutral-600">{t("landing.roleBasedAccessDesc")}</p>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            {roles.map((role, index) => (
              <Card
                key={index}
                className="w-48 text-center hover:shadow-lg transition-shadow border-neutral-400/30 bg-white"
              >
                <CardContent className="pt-6">
                  <div
                    className={`mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full ${role.color} text-white`}
                  >
                    <role.icon className="h-7 w-7" />
                  </div>
                  <h3 className="font-semibold text-neutral-900">{t(role.titleKey)}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary py-20">
        <div className="container text-center">
          <h2 className="text-3xl font-bold text-white sm:text-4xl">{t("landing.readyToStart")}</h2>
          <p className="mt-4 text-lg text-primary-50 max-w-2xl mx-auto">{t("landing.readyToStartDesc")}</p>
          <div className="mt-10 flex flex-col gap-4 sm:flex-row justify-center">
            <Link to="/login">
              <Button
                size="lg"
                variant="secondary"
                className="text-lg px-8 bg-white text-primary hover:bg-neutral-200"
              >
                {t("landing.loginNow")}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-white py-12">
        <div className="container">
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="flex items-center gap-2">
              <Clock className="h-6 w-6 text-primary" />
              <span className="text-lg font-semibold text-primary">Mercury TNA</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-neutral-600">
              <CheckCircle className="h-4 w-4 text-primary" />
              <span>{t("landing.laborLawCompliant")}</span>
            </div>
            <p className="text-sm text-neutral-600">
              {t("landing.copyright", { year: new Date().getFullYear() })}
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage
